import { useCallback, useState } from 'react';
import { Info, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { APP_TYPES } from '@scio3/shared-types';
import type { AppMeta } from '@scio3/shared-types';
import { SafeHtml } from '@/components/shared/SafeHtml';
import { usePromotionalBanner } from '@/hooks/usePromotionalBanner';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { useAuthStore } from '@/stores/auth-store';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';

/**
 * If the promo HTML contains a single <a> wrapping all text
 * (e.g. "<a href='...'>Plain text. CTA!</a>"), pull the plain text outside the
 * link so only the last sentence (the CTA like "Explore Now!") is underlined.
 */
export function splitPromoLink(html: string): string {
  // Match an <a ...>...</a> anywhere in the string, capturing the opening tag and inner text
  const match = html.match(/(<a\s[^]*?>)([^]*?)<\/a>/i);
  if (!match) return html;

  const fullMatch = match[0]; // full <a ...>...</a>
  const aOpen = match[1]; // <a href="...">
  const inner = match[2]; // inner text content
  const lastDotSpace = inner.lastIndexOf('. ');

  if (lastDotSpace === -1) return html;

  const plainPart = inner.slice(0, lastDotSpace + 2);
  const ctaPart = inner.slice(lastDotSpace + 2);

  return html.replace(fullMatch, `${plainPart}${aOpen}${ctaPart}</a>`);
}

export function PromotionalBanner() {
  const { t } = useTranslation();
  const { isVisible, bannerType, bannerHtml, daysLeft, showUpgrade, dismiss } =
    usePromotionalBanner();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);
  const [upgrading, setUpgrading] = useState(false);

  /** Delegate clicks on banner HTML elements by id (matches scio1 dashboard.js onBannerClick) */
  const handleBannerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest('[id]');
      if (!target) return;

      if (target.id === 'openBackgroundMusic') {
        e.preventDefault();
        e.stopPropagation();
        const appMeta = me?.appMetas?.find(
          (m: { appType?: string }) => m.appType === APP_TYPES.BACKGROUND_MUSIC,
        );
        if (!appMeta) return;
        useSmallAppEditorStore.getState().openEditor({
          appMeta: appMeta as AppMeta,
          asset: { appType: APP_TYPES.BACKGROUND_MUSIC },
          options: { isCloseModalAfterSave: true },
        });
      }
    },
    [me],
  );

  if (!isVisible) return null;
  if (bannerType !== 'trial' && !bannerHtml) return null;

  const handleUpgrade = async () => {
    if (upgrading) return;
    setUpgrading(true);
    analyticsUpgradeCta({
      ctaLocation: `promotional_banner_${bannerType}`,
      currentPlan: company?.payment?.plan,
    });
    try {
      // No fixed delay needed — scio1 subscriptionPlanModal now subscribes to
      // 'myCompany' reactively + showStep() guards against null data (#718).
      await openSubscriptionPlan({
        title: t('Change Plan'),
        subtitle: t('Upgrade Required'),
        // Match scio1 dashboard.js openSubscriptionPlanModal: use changePlanTrialToPay for
        // both trial and free plan users from the banner. changePlanPay triggers reactive
        // billing email validation in subscriptionPlanModal.js for accounts without a card.
        action: bannerType === 'paid' ? 'changePlanPay' : 'changePlanTrialToPay',
        sizeModal: 'xl',
        params: {
          arrCssFile: true,
          // Filter plans to standard+ and pre-select proPlus (matches scio1 dashboard.js)
          planFeature: 'proPlus',
          minPlanRequired: 'standard',
        },
      });
    } finally {
      setUpgrading(false);
    }
  };

  if (bannerType === 'trial') {
    return (
      <div
        className="hidden sm:flex w-full items-center justify-center bg-gradient-to-r from-[#17a085] via-primary to-[#1ec9a8] px-6 py-2 text-sm text-white"
        role="alert"
        onClick={handleBannerClick}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Info className="h-5 w-5 shrink-0" />
          <div className="flex flex-col leading-tight">
            <span className="truncate font-bold">
              {t('waitingForTrial', { getDayTrial: daysLeft })}
            </span>
            {bannerHtml && (
              <SafeHtml
                html={splitPromoLink(bannerHtml)}
                as="span"
                className="hidden lg:inline truncate text-xs font-normal text-white/90 [&_a]:text-white [&_a]:underline"
              />
            )}
          </div>
          {showUpgrade && (
            <>
              <span className="hidden md:block h-5 w-px rounded-full bg-white" />
              <Button
                size="sm"
                className="hidden md:inline-flex shrink-0 rounded-md bg-foreground px-5 text-xs font-semibold text-background hover:bg-foreground/90"
                onClick={handleUpgrade}
                disabled={upgrading}
              >
                {upgrading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                {t('Upgrade Now')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="hidden sm:flex h-10 w-full items-center gap-3 bg-primary px-6 text-sm font-medium text-white [&_a]:text-white [&_a]:no-underline [&_a]:hover:underline [&_p]:mb-0 [&_p]:text-sm"
      role="alert"
      onClick={handleBannerClick}
    >
      {/* Spacer to balance dismiss button */}
      {bannerType === 'paid' && <div className="w-6 shrink-0" />}

      <div className="mx-auto flex min-w-0 items-center gap-3">
        {/* Promotional HTML */}
        <SafeHtml html={bannerHtml ?? ''} as="span" className="truncate" />

        {/* Upgrade button — free plan (admin only) */}
        {showUpgrade && (
          <>
            <span className="hidden md:block h-5 w-px bg-white/30" />
            <Button
              size="sm"
              className="hidden md:inline-flex h-7 rounded-md bg-foreground px-4 text-xs text-background hover:bg-foreground/90"
              onClick={(e) => {
                e.stopPropagation();
                handleUpgrade();
              }}
              disabled={upgrading}
            >
              {upgrading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {t('Upgrade')}
            </Button>
          </>
        )}
      </div>

      {/* Close button — paid users only, never for trial/free */}
      {bannerType === 'paid' && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="-mr-3 shrink-0 rounded-full p-1 text-white transition-colors hover:bg-white/15"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          aria-label={t('Dismiss banner')}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      )}
    </div>
  );
}
